import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Shield, Users, ClipboardList, Package, Mail, Globe, FileText, BarChart3, Bot, ArrowRight,
  Lock, UserCheck, FileSearch, Database, Truck, Printer, Calculator, CreditCard, Activity,
  Zap, Server, Code2, RefreshCcw, Layers, TrendingUp, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className={`card-hover group bg-white rounded-2xl p-7 border-2 border-transparent hover:border-${color === 'blue' ? '[#014DB7]' : '[#84CC16]'}/30 shadow-soft hover:shadow-hard shine-effect`}>
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300 ${
      color === 'blue' 
        ? 'bg-[#014DB7]/10 group-hover:bg-[#014DB7]' 
        : 'bg-[#84CC16]/10 group-hover:bg-[#84CC16]'
    }`}>
      <Icon className={`w-7 h-7 transition-colors duration-300 ${
        color === 'blue' ? 'text-[#014DB7] group-hover:text-white' : 'text-[#84CC16] group-hover:text-white'
      }`} />
    </div>
    <h3 className="text-lg font-bold text-[#0A1628] mb-2 group-hover:text-[#014DB7] transition-colors">{title}</h3>
    <p className="text-[#64748B] text-sm leading-relaxed">{description}</p>
  </div>
);

const FeatureSection = ({ icon: Icon, title, description, features, isAlt, color }) => (
  <section className={`py-24 px-6 lg:px-8 ${isAlt ? 'bg-[#F8FAFC]' : 'bg-white'} relative`}>
    {isAlt && <div className="absolute inset-0 grid-pattern opacity-40" />}
    <div className="relative max-w-7xl mx-auto">
      <div className="flex items-center gap-5 mb-12">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-soft ${
          color === 'blue' ? 'bg-[#014DB7]' : 'bg-[#84CC16]'
        }`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-[#0A1628]">{title}</h2>
          <p className="text-[#64748B]">{description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-grid">
        {features.map((feature, index) => (
          <FeatureCard key={index} {...feature} color={index % 2 === 0 ? 'blue' : 'lime'} />
        ))}
      </div>
    </div>
  </section>
);

export const FeaturesPage = () => {
  const [featureToggles, setFeatureToggles] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/api/public/features`)
      .then(res => res.json())
      .then(data => setFeatureToggles(data || {}))
      .catch(() => {});
  }, []);

  const hipaaFeatures = [
    { icon: UserCheck, title: 'Role-Based Access', description: 'Super Admin, Admin, Sales, and Patient roles with granular permissions.' },
    { icon: Lock, title: 'JWT Authentication', description: 'Secure token-based auth with session management.' },
    { icon: Users, title: 'Admin Impersonation', description: 'Securely troubleshoot without password sharing.' },
    { icon: FileSearch, title: 'Audit Logging', description: 'Every action tracked with user, timestamp, and details.' },
    { icon: Server, title: 'Encrypted Storage', description: 'iDrive E2 (S3-compatible) encrypted documents.' },
    { icon: Zap, title: 'Magic Links', description: 'Secure, time-limited access for physicians.' },
  ];

  const patientFeatures = [
    { icon: Users, title: 'Patient CRM', description: 'Full profiles with demographics, insurance, medical history.' },
    { icon: FileText, title: 'Document Management', description: 'Folder organization with prescription and CMN tracking.' },
    { icon: ClipboardList, title: 'Kanban Board', description: 'Visual pipeline with drag-and-drop lead progression.' },
    { icon: Activity, title: 'Lead Conversion', description: 'One-click conversion with data preservation.' },
    { icon: Globe, title: 'Lead Capture', description: 'Public intake forms with UTM tracking.' },
  ];

  const orderFeatures = [
    { icon: ClipboardList, title: 'Order Processing', description: 'Complete lifecycle with multi-supplier routing.' },
    { icon: Truck, title: 'Multi-Supplier Routing', description: 'Automatically route orders to the right supplier.' },
    { icon: RefreshCcw, title: 'Status Tracking', description: 'Real-time order status updates from submission to delivery.' },
    { icon: CreditCard, title: 'Billing Integration', description: 'Seamless invoicing and payment tracking per order.' },
    { icon: FileText, title: 'Documentation', description: 'Auto-generate CMNs, prescriptions, and delivery tickets.' },
    { icon: Activity, title: 'Analytics', description: 'Track fulfillment rates, processing times, and bottlenecks.' },
  ];

  const catalogFeatures = [
    { icon: Database, title: '17 DME Categories', description: 'Emergency, Mobility, Hospital Beds, CPAP, Prosthetics, more.' },
    { icon: Package, title: '146+ Products', description: 'Pre-loaded DME equipment with HCPCS codes.' },
    { icon: Truck, title: '10 Suppliers', description: 'NikoHealth, McKesson, Medline, Rotech, AdaptHealth, more.' },
    { icon: RefreshCcw, title: 'Real-Time Inventory', description: 'Query supplier stock and compare availability.' },
    { icon: CreditCard, title: 'Auto Pricing', description: 'List, contract, and Medicare allowable rates.' },
    { icon: Layers, title: 'Demo Mode', description: 'Full preview before API configuration.' },
  ];

  const marketingFeatures = [
    { icon: Users, title: 'Subscriber Management', description: 'Import/export CSV, manual entry, auto capture.' },
    { icon: ClipboardList, title: 'Mailing Lists', description: 'Targeted lists for Patients, Leads, Team, Custom.' },
    { icon: Bot, title: 'AI Newsletter', description: 'One-click AI content generation with GPT-5.2.' },
    { icon: FileText, title: 'Rich Editor', description: 'Full WYSIWYG with formatting, images, links.' },
    { icon: Mail, title: 'SMTP Integration', description: 'Production-ready email with any provider.' },
    { icon: Activity, title: 'Auto-Subscribe', description: 'New leads and team auto-added to lists.' },
  ];

  const seoFeatures = [
    { icon: Globe, title: '50 US States', description: 'Complete coverage with unique SEO content.' },
    { icon: Database, title: '3,142 Counties', description: 'County-level pages for local search.' },
    { icon: Database, title: '31,285 Cities', description: 'City-level targeting for DME searches.' },
    { icon: FileText, title: 'Dynamic Sitemap', description: 'Auto-includes products, categories, locations.' },
    { icon: Code2, title: 'robots.txt', description: 'Control search engine crawling.' },
    { icon: BarChart3, title: 'SEO Dashboard', description: 'Live sitemap preview and tracking.' },
  ];

  const analyticsFeatures = [
    { icon: Activity, title: 'Conversion Rates', description: 'Track and optimize your sales funnel.' },
    { icon: CreditCard, title: 'Pipeline Value', description: 'Monitor revenue in real-time.' },
    { icon: Users, title: 'Team Performance', description: 'Individual and team metrics.' },
    { icon: Globe, title: 'Marketing Analytics', description: 'UTM tracking and attribution.' },
    { icon: Calculator, title: 'Accounting', description: 'Expense tracking and summaries.' },
    { icon: BarChart3, title: 'Revenue Reports', description: 'Comprehensive financial analytics.' },
  ];

  const stats = [
    { value: '17', label: 'DME Categories' },
    { value: '146+', label: 'Products' },
    { value: '10', label: 'Suppliers' },
    { value: '34K+', label: 'Location Pages' },
    { value: '50', label: 'US States' },
    { value: '3,142', label: 'Counties' },
    { value: '100+', label: 'API Endpoints' },
    { value: '4', label: 'User Roles' },
  ];

  return (
    <div className="min-h-screen pt-48">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-8 gradient-animated relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute top-20 left-10 w-80 h-80 bg-[#014DB7]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#84CC16]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto text-center stagger-children">
          <div className="badge-blue mx-auto mb-6">
            <Sparkles className="w-4 h-4" />
            <span>Full Feature Set</span>
          </div>
          <h1 className="text-display text-[#0A1628] mb-6">
            Powerful <span className="gradient-text-mixed">Features</span> for DME
          </h1>
          <p className="text-subtitle max-w-3xl mx-auto mb-10">
            Everything you need to manage patients, orders, and compliance. Built by healthcare professionals.
          </p>
          <Link to="/demo">
            <Button data-testid="features-demo-btn" className="btn-lift bg-[#014DB7] text-white hover:bg-[#003A8C] rounded-xl px-10 py-5 text-lg font-bold shadow-glow-blue group">
              See It In Action
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Feature Sections */}
      <FeatureSection icon={Shield} title="Enterprise HIPAA Compliance" description="Security built into every layer." features={hipaaFeatures} isAlt={false} color="blue" />
      {featureToggles.organic_seo !== false && (
        <FeatureSection icon={Globe} title="Organic SEO Services" description="34,477 organically generated location pages for more than 50 US states." features={seoFeatures} isAlt={true} color="lime" />
      )}
      <FeatureSection icon={BarChart3} title="Analytics & Reporting" description="Data-driven insights." features={analyticsFeatures} isAlt={false} color="blue" />
      {featureToggles.patient_crm !== false && (
        <FeatureSection icon={Users} title="Patient & CRM Management" description="Manage patients, leads, and contacts." features={patientFeatures} isAlt={true} color="lime" />
      )}
      {featureToggles.order_processing !== false && (
        <FeatureSection icon={ClipboardList} title="Order Processing" description="Complete order lifecycle management." features={orderFeatures} isAlt={false} color="blue" />
      )}
      <FeatureSection icon={Package} title="DME Product Catalog" description="17 categories, 146+ products, 10 suppliers." features={catalogFeatures} isAlt={true} color="lime" />
      <FeatureSection icon={Mail} title="Marketing & Newsletter" description="AI content and campaign management." features={marketingFeatures} isAlt={false} color="blue" />

      {/* Stats */}
      <section className="py-24 px-6 lg:px-8 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#014DB7]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#84CC16]/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16">By The <span className="text-[#84CC16]">Numbers</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 stagger-grid">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/5 rounded-2xl border border-white/10 card-hover">
                <p className={`text-5xl font-extrabold mb-2 ${index % 2 === 0 ? 'text-[#014DB7]' : 'text-[#84CC16]'}`}>{stat.value}</p>
                <p className="text-white/60 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8 bg-white relative">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="relative max-w-4xl mx-auto text-center stagger-children">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
            Ready to <span className="gradient-text-blue">Get Started</span>?
          </h2>
          <p className="text-subtitle mb-10">
            Schedule a personalized demo to see how DME R'US transforms your operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button data-testid="features-cta-demo-btn" className="btn-lift bg-[#014DB7] text-white hover:bg-[#003A8C] rounded-xl px-10 py-5 text-lg font-bold shadow-glow-blue group">
                Request Demo
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="features-cta-contact-btn" variant="outline" className="btn-lift border-2 border-[#0A1628]/20 hover:border-[#014DB7] hover:bg-[#014DB7]/5 rounded-xl px-10 py-5 text-lg font-bold">
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
